import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faEdit,
  faTrash,
  faBox,
  faTags,
  faDatabase,
  faSearch,
  faClose,
  faSpinner,
  faCheckCircle,
  faCancel,
  faTrashAlt,
  faImage,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import defaultProductImg from "./images/product.png";
import { useNavigationEvent } from "navigation-react";

// Fetcher for SWR
const fetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/vnd.api+json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  return response.json();
};

// Helper function to escape HTML
const escapeHtml = (text) => {
  if (!text) return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

export default function ProductRegister() {
  const { stateNavigator } = useNavigationEvent();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);

  // Category Modal States
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  // Supplier Modal States
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [newSupplierForm, setNewSupplierForm] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
  });
  const [addingSupplier, setAddingSupplier] = useState(false);

  const [newAddProductForm, setNewAddProductForm] = useState({
    productname: "",
    sku: "",
    category: "",
    price: "",
    stockquantity: "",
    supplier: "",
    productimage: null,
    description: "",
    stockthreshold: "",
  });

  const [editProductForm, setEditProductForm] = useState({
    productname: "",
    sku: "",
    category: "",
    price: "",
    stockquantity: "",
    supplier: "",
    productimage: null,
    description: "",
    stockthreshold: "",
    currentImageUrl: null,
    currentImageId: null,
  });
  const [currentEditProductId, setCurrentEditProductId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch products data
  const { data: productList, mutate: mutateProducts } = useSWR(
    `${process.env.REACT_APP_ADD_PRODUCT_API}?sort=-changed&include=field_category,field_supplier,field_product_image,field_barcode_image&page[limit]=1000`,
    fetcher,
  );
  console.log("Fetched product list:", productList);

  // Create a supplier lookup object
  const supplierLookup =
    productList?.included
      ?.filter((item) => item.type === "node--supplier")
      .reduce((acc, supplier) => {
        acc[supplier.id] = supplier.attributes.title;
        return acc;
      }, {}) || {};

  // Create image URL lookup
  const getImageUrl = (product) => {
    const imageRel = product.relationships?.field_product_image?.data;
    if (!imageRel) return defaultProductImg;

    const imageObj = productList?.included?.find(
      (inc) => inc.type === "file--file" && inc.id === imageRel.id,
    );

    if (imageObj?.attributes?.uri?.url) {
      const imageUrl = imageObj.attributes.uri.url;
      if (imageUrl.startsWith("http")) {
        return imageUrl;
      }
      return `${process.env.REACT_APP_BASE_URL}${imageUrl}`;
    }

    return defaultProductImg;
  };

  // Get barcode image URL for a product
  const getBarcodeImageUrl = (product) => {
    const barcodeRel = product.relationships?.field_barcode_image?.data;
    if (!barcodeRel) return null;

    const barcodeObj = productList?.included?.find(
      (inc) => inc.type === "file--file" && inc.id === barcodeRel.id,
    );

    if (barcodeObj?.attributes?.uri?.url) {
      const barcodeUrl = barcodeObj.attributes.uri.url;
      if (barcodeUrl.startsWith("http")) {
        return barcodeUrl;
      }
      return `${process.env.REACT_APP_BASE_URL}${barcodeUrl}`;
    }

    return null;
  };

  // Get image info for a product (URL and ID)
  const getProductImageInfo = (product) => {
    const imageRel = product.relationships?.field_product_image?.data;
    if (!imageRel) return { url: defaultProductImg, id: null };

    const imageObj = productList?.included?.find(
      (inc) => inc.type === "file--file" && inc.id === imageRel.id,
    );

    if (imageObj?.attributes?.uri?.url) {
      let imageUrl = imageObj.attributes.uri.url;
      if (!imageUrl.startsWith("http")) {
        imageUrl = `${process.env.REACT_APP_BASE_URL}${imageUrl}`;
      }
      return { url: imageUrl, id: imageRel.id };
    }

    return { url: defaultProductImg, id: null };
  };

  // Handle printing barcode
  const handlePrintBarcode = (product) => {
    const barcodeUrl = getBarcodeImageUrl(product);

    if (!barcodeUrl) {
      setErrorMessage("No barcode image available for this product");
      setModalError(true);
      return;
    }

    // Open a new window for printing
    const printWindow = window.open("", "_blank", "width=200,height=100");

    if (!printWindow) {
      setErrorMessage("Please allow pop-ups to print barcodes");
      setModalError(true);
      return;
    }

    // Write the content to the new window
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Print Barcode</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          margin: 0;
          padding: 0;
          background: white;
        }
        
        .barcode-container {
          width: 40mm;
          height: 20mm;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        
        .barcode-container img {
          display: block;
          width: 40mm;
          height: 20mm;
          object-fit: contain;
        }
        
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          .barcode-container {
            width: 40mm;
            height: 20mm;
          }
          
          .barcode-container img {
            width: 40mm;
            height: 20mm;
          }
        }
        
        @page {
          size: 40mm 20mm;
          margin: 0mm;
        }
      </style>
    </head>
    <body>
      <div class="barcode-container">
        <img src="${barcodeUrl}" alt="Barcode" />
      </div>
    </body>
    </html>
  `);

    printWindow.document.close();

    // Wait for content to load then print
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
      }, 500);
    }, 500);
  };

  const { data: categoryData, mutate: mutateCategories } = useSWR(
    `${process.env.REACT_APP_CATEGORY_API}?sort=name&page[limit]=1000`,
    fetcher,
  );

  const categoryItems =
    categoryData?.data?.map((term) => ({
      label: term.attributes.name,
      value: term.id,
    })) || [];

  const { data: supplierData, mutate: mutateSuppliers } = useSWR(
    `${process.env.REACT_APP_SUPPLIER_API}?sort=title&page[limit]=1000`,
    fetcher,
  );

  const supplierItems =
    supplierData?.data?.map((suppitem) => ({
      label: suppitem.attributes.title,
      value: suppitem.id,
    })) || [];

  const getCategoryName = (product) => {
    const categoryRel = product.relationships?.field_category?.data;
    if (!categoryRel) return "Uncategorized";

    const categoryObj = productList?.included?.find(
      (inc) =>
        inc.type === "taxonomy_term--category" && inc.id === categoryRel.id,
    );

    return categoryObj?.attributes?.name || "Uncategorized";
  };

  // Filter and sort products
  const filteredAndSortedProducts = () => {
    if (!productList?.data) return [];

    let filtered = productList.data.filter((product) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        product.attributes.title?.toLowerCase().includes(searchLower) ||
        product.attributes.field_sku?.toLowerCase().includes(searchLower) ||
        product.attributes.field_gtin?.toLowerCase().includes(searchLower) ||
        product.attributes.field_price?.toString().includes(searchLower)
      );
    });

    return filtered;
  };

  // Pagination
  const products = filteredAndSortedProducts();
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = products.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const fetchCsrfToken = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch CSRF token");
    }

    return await response.text();
  };

  const uploadProductImage = async (file) => {
    if (!file) return null;

    try {
      const csrfToken = await fetchCsrfToken();

      const uploadUrl = `${process.env.REACT_APP_BASE_URL}/file/upload/node/product/field_product_image`;

      const response = await fetch(uploadUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/octet-stream",
          "Content-Disposition": `file; filename="${file.name}"`,
          "X-CSRF-Token": csrfToken,
        },
        body: file,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload error:", errorText);
        throw new Error(`File upload failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log("Full upload response:", result);

      let fileUuid = null;

      if (result.uuid && result.uuid[0] && result.uuid[0].value) {
        fileUuid = result.uuid[0].value;
      } else if (result.data && result.data.id) {
        fileUuid = result.data.id;
      } else if (result.id) {
        fileUuid = result.id;
      } else if (result.uuid) {
        fileUuid = result.uuid;
      }

      if (!fileUuid) {
        console.error("Could not extract UUID from response:", result);
        throw new Error("Failed to extract file UUID from response");
      }

      console.log("Extracted file UUID:", fileUuid);

      return {
        id: fileUuid,
        type: "file--file",
      };
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  // Function to add a new category
  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!newCategoryName.trim()) {
      setErrorMessage("Category name is required");
      setModalError(true);
      return;
    }

    setAddingCategory(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setAddingCategory(false);
      return;
    }

    const addCategoryPayload = {
      data: {
        type: "taxonomy_term--category",
        attributes: {
          name: newCategoryName,
        },
      },
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_CATEGORY_API}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(addCategoryPayload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Category created successfully:", result);

        // Refresh categories data
        if (mutateCategories) {
          mutateCategories();
        }

        setNewCategoryName("");
        setShowAddCategoryModal(false);
        setSuccessMessage("Category added successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Category creation error:", errorData);
        setErrorMessage(`Failed to create category: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error creating category:", err);
      setErrorMessage(
        err.message || "An error occurred while creating the category",
      );
      setModalError(true);
    }
    setAddingCategory(false);
  };

  // Function to add a new supplier
  const handleAddSupplier = async (e) => {
    e.preventDefault();

    if (!newSupplierForm.name.trim()) {
      setErrorMessage("Supplier name is required");
      setModalError(true);
      return;
    }

    setAddingSupplier(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setAddingSupplier(false);
      return;
    }

    const addSupplierPayload = {
      data: {
        type: "node--supplier",
        attributes: {
          title: newSupplierForm.name,
          field_address: newSupplierForm.address || "",
          field_phone: newSupplierForm.phone || "",
          field_supplier_email: newSupplierForm.email || "",
        },
      },
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_SUPPLIER_API}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(addSupplierPayload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Supplier created successfully:", result);

        // Refresh suppliers data
        if (mutateSuppliers) {
          mutateSuppliers();
        }

        setNewSupplierForm({
          name: "",
          address: "",
          phone: "",
          email: "",
        });
        setShowAddSupplierModal(false);
        setSuccessMessage("Supplier added successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Supplier creation error:", errorData);
        setErrorMessage(`Failed to create supplier: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error creating supplier:", err);
      setErrorMessage(
        err.message || "An error occurred while creating the supplier",
      );
      setModalError(true);
    }
    setAddingSupplier(false);
  };

  // Function to remove image from product (set to null)
  const removeImageFromProduct = async () => {
    if (!currentEditProductId) return;

    setLoading(true);

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setLoading(false);
      return;
    }

    const updatePayload = {
      data: {
        type: "node--product",
        id: currentEditProductId,
        attributes: {
          title: editProductForm.productname,
          field_sku: editProductForm.sku || "",
          field_price: parseFloat(editProductForm.price) || 0,
          field_stock_quantity: parseInt(editProductForm.stockquantity) || 0,
          field_description: editProductForm.description || "",
          field_low_stock_threshold:
            parseInt(editProductForm.stockthreshold) || 0,
        },
        relationships: {
          field_category: {
            data: {
              type: "taxonomy_term--category",
              id: editProductForm.category,
            },
          },
          field_supplier: {
            data: {
              type: "node--supplier",
              id: editProductForm.supplier,
            },
          },
          field_product_image: {
            data: null,
          },
        },
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ADD_PRODUCT_API}/${currentEditProductId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(updatePayload),
        },
      );

      if (response.ok) {
        setEditProductForm((prev) => ({
          ...prev,
          currentImageUrl: defaultProductImg,
          currentImageId: null,
          productimage: null,
        }));

        setSuccessMessage("Product image removed successfully!");
        setModalSuccessMsg(true);

        if (mutateProducts) {
          mutateProducts();
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(
          `Failed to remove image: ${errorData.errors?.[0]?.detail || response.statusText}`,
        );
        setModalError(true);
      }
    } catch (err) {
      console.error("Error removing image:", err);
      setErrorMessage("An error occurred while removing the image");
      setModalError(true);
    }
    setLoading(false);
  };

  const handleAddNewProduct = async (e) => {
    e.preventDefault();

    if (!newAddProductForm.productname.trim()) {
      setErrorMessage("Product name is required");
      setModalError(true);
      return;
    }

    setLoading(true);

    let fileData = null;

    if (newAddProductForm.productimage) {
      console.log("Starting product image upload...");
      setUploadingImage(true);

      try {
        fileData = await uploadProductImage(newAddProductForm.productimage);

        if (fileData && fileData.id) {
          console.log("Product Image UUID captured:", fileData.id);
        } else {
          console.warn(
            "Product image upload failed, proceeding without image.",
          );
        }
      } catch (error) {
        console.error("Image upload error:", error);
        setErrorMessage("Failed to upload image: " + error.message);
        setModalError(true);
        setUploadingImage(false);
        setLoading(false);
        return;
      }

      setUploadingImage(false);
    }

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
      console.log("CSRF Token fetched for product creation");
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setLoading(false);
      return;
    }

    const addproductpayload = {
      data: {
        type: "node--product",
        attributes: {
          title: newAddProductForm.productname,
          field_sku: newAddProductForm.sku || "",
          field_price: parseFloat(newAddProductForm.price) || 0,
          field_stock_quantity: parseInt(newAddProductForm.stockquantity) || 0,
          field_low_stock_threshold:
            parseInt(newAddProductForm.stockthreshold) || 0,
          field_description: newAddProductForm.description || "",
        },
        relationships: {
          field_category: {
            data: {
              type: "taxonomy_term--category",
              id: newAddProductForm.category,
            },
          },
          field_supplier: {
            data: {
              type: "node--supplier",
              id: newAddProductForm.supplier,
            },
          },
        },
      },
    };

    if (fileData && fileData.id) {
      addproductpayload.data.relationships.field_product_image = {
        data: {
          type: "file--file",
          id: fileData.id,
        },
      };
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_ADD_PRODUCT_API}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          Accept: "application/vnd.api+json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify(addproductpayload),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Product created successfully:", result);

        if (mutateProducts) {
          mutateProducts();
        }

        setNewAddProductForm({
          productname: "",
          sku: "",
          category: "",
          price: "",
          stockquantity: "",
          supplier: "",
          productimage: null,
          description: "",
          stockthreshold: "",
        });
        setShowAddProductModal(false);
        setSuccessMessage("Product added successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Product creation error:", errorData);
        setErrorMessage(`Failed to create product: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error creating product:", err);
      setErrorMessage(
        err.message || "An error occurred while creating the product",
      );
      setModalError(true);
    }
    setLoading(false);
  };

  const handleDelete = async (productId) => {
    try {
      if (window.confirm("Are you sure you want to delete this product?")) {
        let csrfToken;
        try {
          csrfToken = await fetchCsrfToken();
          console.log("CSRF Token fetched for product deletion");
        } catch (error) {
          console.error("Failed to fetch CSRF token:", error);
          setErrorMessage("Authentication error. Please refresh the page.");
          setModalError(true);
          return;
        }

        const res = await fetch(
          `${process.env.REACT_APP_ADD_PRODUCT_API}/${productId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": csrfToken,
            },
            credentials: "include",
          },
        );

        if (res.ok) {
          setSuccessMessage("Product deleted successfully!");
          setModalSuccessMsg(true);
          mutateProducts();
        } else {
          const errorData = await res.json().catch(() => ({}));
          let errorMessageText = res.statusText;
          if (errorData.errors?.[0]?.detail) {
            errorMessageText = errorData.errors[0].detail;
          }
          setErrorMessage(`Failed to delete product: ${errorMessageText}`);
          setModalError(true);
        }
      }
    } catch (error) {
      console.error("Network error:", error);
      setErrorMessage("Network error. Please check your connection.");
      setModalError(true);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!currentEditProductId) {
      setErrorMessage("No product selected for editing");
      setModalError(true);
      return;
    }

    if (!editProductForm.productname.trim()) {
      setErrorMessage("Product name is required");
      setModalError(true);
      return;
    }

    setLoading(true);

    let fileData = null;

    if (
      editProductForm.productimage &&
      typeof editProductForm.productimage === "object"
    ) {
      console.log("Starting product image upload for edit...");
      setUploadingImage(true);

      try {
        fileData = await uploadProductImage(editProductForm.productimage);

        if (fileData && fileData.id) {
          console.log("Product Image UUID captured:", fileData.id);
        }
      } catch (error) {
        console.error("Image upload error:", error);
        setErrorMessage("Failed to upload image: " + error.message);
        setModalError(true);
        setUploadingImage(false);
        setLoading(false);
        return;
      }

      setUploadingImage(false);
    }

    let csrfToken;
    try {
      csrfToken = await fetchCsrfToken();
      console.log("CSRF Token fetched for product edit");
    } catch (error) {
      console.error("Failed to fetch CSRF token:", error);
      setErrorMessage("Authentication error. Please refresh the page.");
      setModalError(true);
      setLoading(false);
      return;
    }

    const editproductpayload = {
      data: {
        type: "node--product",
        id: currentEditProductId,
        attributes: {
          title: editProductForm.productname,
          field_sku: editProductForm.sku || "",
          field_price: parseFloat(editProductForm.price) || 0,
          field_stock_quantity: parseInt(editProductForm.stockquantity) || 0,
          field_description: editProductForm.description || "",
          field_low_stock_threshold:
            parseInt(editProductForm.stockthreshold) || 0,
        },
        relationships: {
          field_category: {
            data: {
              type: "taxonomy_term--category",
              id: editProductForm.category,
            },
          },
          field_supplier: {
            data: {
              type: "node--supplier",
              id: editProductForm.supplier,
            },
          },
        },
      },
    };

    if (fileData && fileData.id) {
      editproductpayload.data.relationships.field_product_image = {
        data: {
          type: "file--file",
          id: fileData.id,
        },
      };
    } else if (
      editProductForm.currentImageId &&
      !editProductForm.productimage
    ) {
      editproductpayload.data.relationships.field_product_image = {
        data: {
          type: "file--file",
          id: editProductForm.currentImageId,
        },
      };
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ADD_PRODUCT_API}/${currentEditProductId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(editproductpayload),
        },
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Product edited successfully:", result);

        if (mutateProducts) {
          mutateProducts();
        }

        setShowEditProductModal(false);
        setCurrentEditProductId(null);
        setEditProductForm({
          productname: "",
          sku: "",
          category: "",
          price: "",
          stockquantity: "",
          supplier: "",
          productimage: null,
          description: "",
          stockthreshold: "",
          currentImageUrl: null,
          currentImageId: null,
        });
        setSuccessMessage("Product edited successfully!");
        setModalSuccessMsg(true);
      } else {
        const errorData = await response.json();
        let errorMessageText = response.statusText;
        if (errorData.errors?.[0]?.detail) {
          errorMessageText = errorData.errors[0].detail;
        }
        console.error("Product edit error:", errorData);
        setErrorMessage(`Failed to edit product: ${errorMessageText}`);
        setModalError(true);
      }
    } catch (err) {
      console.error("Error editing product:", err);
      setErrorMessage(
        err.message || "An error occurred while editing the product",
      );
      setModalError(true);
    }
    setLoading(false);
  };

  // Calculate statistics
  const calculateStatistics = () => {
    if (!productList?.data)
      return { totalProducts: 0, totalStock: 0, totalValue: 0 };

    const products = productList.data;
    const totalProducts = products.length;
    const totalStock = products.reduce((sum, product) => {
      return sum + (parseInt(product.attributes.field_stock_quantity) || 0);
    }, 0);

    const totalValue = products.reduce((sum, product) => {
      const quantity = parseInt(product.attributes.field_stock_quantity) || 0;
      const price = parseFloat(product.attributes.field_price) || 0;
      return sum + quantity * price;
    }, 0);

    return { totalProducts, totalStock, totalValue };
  };

  const { totalProducts, totalStock, totalValue } = calculateStatistics();

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="products-container" style={styles.container}>
          <TopActionMenus
            AddTitle="Product Register"
            buttonLabel="Add Product"
            icon={faAdd}
            onClick={() => setShowAddProductModal(true)}
            buttonCatRegLabel="Category Register"
            onClickCategoryRegister={() =>
              stateNavigator.navigate("categoryregister")
            }
            buttonSupRegLabel="Supplier Register"
            onClickSupplierRegister={() =>
              stateNavigator.navigate("supplierregister")
            }
          />

          {/* STATISTICS */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faBox} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{totalProducts}</h3>
                <p style={styles.statLabel}>Total Products</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faDatabase} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>{totalStock}</h3>
                <p style={styles.statLabel}>Total Stock Quantity</p>
              </div>
            </div>
            <div style={styles.statCard}>
              <FontAwesomeIcon icon={faTags} style={styles.statIcon} />
              <div>
                <h3 style={styles.statValue}>GH₵{totalValue.toFixed(2)}</h3>
                <p style={styles.statLabel}>Total Stock Value</p>
              </div>
            </div>
          </div>

          {/* SEARCH BAR */}
          <div style={styles.searchBar}>
            <div style={styles.searchContainer}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search products by name, SKU, GTIN or price..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* PRODUCTS LIST TABLE */}
          <div style={styles.card}>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>SKU</th>
                    <th>GTIN/Barcode</th>
                    <th>Price (GHC)</th>
                    <th>Category</th>
                    <th>Stock</th>
                    <th>Low Stock Threshold</th>
                    <th>Supplier</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!productList ? (
                    <tr>
                      <td
                        colSpan="10"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        products...
                      </td>
                    </tr>
                  ) : paginatedProducts.length === 0 ? (
                    <tr>
                      <td
                        colSpan="10"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        {searchTerm
                          ? "No products match your search"
                          : "No products found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedProducts.map((product) => {
                      const imageInfo = getProductImageInfo(product);
                      return (
                        <tr key={product.id}>
                          <td>
                            <div style={styles.prodimgNname}>
                              <img
                                src={imageInfo.url}
                                alt={product.attributes.title}
                                style={styles.productImage}
                                onError={(e) => {
                                  e.target.src = defaultProductImg;
                                }}
                              />
                              {product.attributes.title}
                            </div>
                          </td>
                          <td>{product.attributes.field_sku}</td>
                          <td>{product.attributes.field_gtin}</td>
                          <td>
                            {parseFloat(
                              product.attributes.field_price || 0,
                            ).toFixed(2)}
                          </td>
                          <td>{getCategoryName(product)}</td>
                          <td>{product.attributes.field_stock_quantity}</td>
                          <td>
                            {product.attributes.field_low_stock_threshold ||
                              "0"}
                          </td>
                          <td>
                            {supplierLookup[
                              product.relationships?.field_supplier?.data?.id
                            ] || "No Supplier"}
                          </td>
                          <td>
                            {new Date(
                              product.attributes.changed,
                            ).toLocaleDateString()}
                          </td>
                          <td style={styles.actionBTN}>
                            <button
                              style={styles.printButton}
                              onClick={() => handlePrintBarcode(product)}
                              title="Print Barcode"
                            >
                              <FontAwesomeIcon icon={faPrint} />
                            </button>
                            <button
                              style={styles.editButton}
                              onClick={() => {
                                const productImageInfo =
                                  getProductImageInfo(product);
                                setEditProductForm({
                                  productname: product.attributes.title || "",
                                  sku: product.attributes.field_sku || "",
                                  category:
                                    product.relationships?.field_category?.data
                                      ?.id || "",
                                  price: product.attributes.field_price || "",
                                  stockquantity:
                                    product.attributes.field_stock_quantity ||
                                    "",
                                  supplier:
                                    product.relationships?.field_supplier?.data
                                      ?.id || "",
                                  productimage: null,
                                  description:
                                    product.attributes.field_description
                                      ?.value || "",
                                  stockthreshold:
                                    product.attributes
                                      .field_low_stock_threshold || "",
                                  currentImageUrl: productImageInfo.url,
                                  currentImageId: productImageInfo.id,
                                });
                                setCurrentEditProductId(product.id);
                                setShowEditProductModal(true);
                              }}
                              title="Edit Product"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              style={styles.deleteButton}
                              onClick={() => handleDelete(product.id)}
                              title="Delete Product"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Add Product Modal */}
            {showAddProductModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content">
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Add New Product</p>
                      <button
                        className="close-modal"
                        onClick={() => setShowAddProductModal(false)}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form
                      onSubmit={handleAddNewProduct}
                      className="edit-patient-form"
                    >
                      <div className="form-group">
                        <label>Product Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Product Name"
                          value={newAddProductForm.productname}
                          onChange={(e) =>
                            setNewAddProductForm({
                              ...newAddProductForm,
                              productname: e.target.value,
                            })
                          }
                          required
                        />
                      </div>

                      <div style={styles.formgroupwrapper}>
                        <div className="form-group">
                          <label>Category *</label>
                          <div style={styles.selectWithButton}>
                            <select
                              value={newAddProductForm.category}
                              onChange={(e) =>
                                setNewAddProductForm({
                                  ...newAddProductForm,
                                  category: e.target.value,
                                })
                              }
                              required
                              style={styles.selectField}
                            >
                              <option value="">Select Category</option>
                              {categoryItems.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              style={styles.inlineAddButton}
                              onClick={() => setShowAddCategoryModal(true)}
                              title="Add New Category"
                            >
                              <FontAwesomeIcon icon={faAdd} />
                            </button>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Supplier *</label>
                          <div style={styles.selectWithButton}>
                            <select
                              value={newAddProductForm.supplier}
                              onChange={(e) =>
                                setNewAddProductForm({
                                  ...newAddProductForm,
                                  supplier: e.target.value,
                                })
                              }
                              required
                              style={styles.selectField}
                            >
                              <option value="">Select Supplier</option>
                              {supplierItems.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              style={styles.inlineAddButton}
                              onClick={() => setShowAddSupplierModal(true)}
                              title="Add New Supplier"
                            >
                              <FontAwesomeIcon icon={faAdd} />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div style={styles.formgroupwrapper}>
                        <div className="form-group">
                          <label>SKU *</label>
                          <input
                            type="text"
                            placeholder="Enter Product SKU"
                            value={newAddProductForm.sku}
                            onChange={(e) =>
                              setNewAddProductForm({
                                ...newAddProductForm,
                                sku: e.target.value,
                              })
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Price (GHC) *</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Enter Product Price"
                            value={newAddProductForm.price}
                            onChange={(e) =>
                              setNewAddProductForm({
                                ...newAddProductForm,
                                price: e.target.value,
                              })
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Stock Quantity *</label>
                          <input
                            type="number"
                            placeholder="Enter Stock Quantity"
                            value={newAddProductForm.stockquantity}
                            onChange={(e) =>
                              setNewAddProductForm({
                                ...newAddProductForm,
                                stockquantity: e.target.value,
                              })
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Stock Threshold *</label>
                          <input
                            type="number"
                            placeholder="Enter Stock Threshold"
                            value={newAddProductForm.stockthreshold}
                            onChange={(e) =>
                              setNewAddProductForm({
                                ...newAddProductForm,
                                stockthreshold: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Product Image</label>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setNewAddProductForm({
                                ...newAddProductForm,
                                productimage: file,
                              });
                            }
                          }}
                        />
                        {newAddProductForm.productimage && (
                          <div style={{ marginTop: "10px" }}>
                            <img
                              src={URL.createObjectURL(
                                newAddProductForm.productimage,
                              )}
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                border: "1px solid #ddd",
                              }}
                            />
                            <p
                              style={{
                                fontSize: "12px",
                                marginTop: "5px",
                                color: "#666",
                              }}
                            >
                              Selected: {newAddProductForm.productimage.name}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Product Description</label>
                        <textarea
                          placeholder="Enter Product Description"
                          maxLength={500}
                          style={{ height: 80 }}
                          value={newAddProductForm.description}
                          onChange={(e) =>
                            setNewAddProductForm({
                              ...newAddProductForm,
                              description: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={loading || uploadingImage}
                          style={styles.button}
                        >
                          {loading || uploadingImage ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Add Product"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Add Category Modal */}
            {showAddCategoryModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content" style={{ maxWidth: "400px" }}>
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Add New Category</p>
                      <button
                        className="close-modal"
                        onClick={() => {
                          setShowAddCategoryModal(false);
                          setNewCategoryName("");
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form
                      onSubmit={handleAddCategory}
                      className="edit-patient-form"
                    >
                      <div className="form-group">
                        <label>Category Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Category Name"
                          value={newCategoryName}
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={addingCategory}
                          style={styles.button}
                        >
                          {addingCategory ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Add Category"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Add Supplier Modal */}
            {showAddSupplierModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content" style={{ maxWidth: "500px" }}>
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Add New Supplier</p>
                      <button
                        className="close-modal"
                        onClick={() => {
                          setShowAddSupplierModal(false);
                          setNewSupplierForm({
                            name: "",
                            address: "",
                            phone: "",
                            email: "",
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form
                      onSubmit={handleAddSupplier}
                      className="edit-patient-form"
                    >
                      <div className="form-group">
                        <label>Supplier Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Supplier Name"
                          value={newSupplierForm.name}
                          onChange={(e) =>
                            setNewSupplierForm({
                              ...newSupplierForm,
                              name: e.target.value,
                            })
                          }
                          required
                          autoFocus
                        />
                      </div>

                      <div className="form-group">
                        <label>Address</label>
                        <input
                          type="text"
                          placeholder="Enter Address"
                          value={newSupplierForm.address}
                          onChange={(e) =>
                            setNewSupplierForm({
                              ...newSupplierForm,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div style={styles.formgroupwrapper}>
                        <div className="form-group">
                          <label>Phone</label>
                          <input
                            type="text"
                            placeholder="Enter Phone Number"
                            value={newSupplierForm.phone}
                            onChange={(e) =>
                              setNewSupplierForm({
                                ...newSupplierForm,
                                phone: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            placeholder="Enter Email Address"
                            value={newSupplierForm.email}
                            onChange={(e) =>
                              setNewSupplierForm({
                                ...newSupplierForm,
                                email: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={addingSupplier}
                          style={styles.button}
                        >
                          {addingSupplier ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Add Supplier"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Product Modal */}
            {showEditProductModal && (
              <div className="modal">
                <div className="overlay"></div>
                <div className="modal-content">
                  <div className="modal-body">
                    <div className="modal-header">
                      <p>Edit Product</p>
                      <button
                        className="close-modal"
                        onClick={() => {
                          setShowEditProductModal(false);
                          setCurrentEditProductId(null);
                          setEditProductForm({
                            productname: "",
                            sku: "",
                            category: "",
                            price: "",
                            stockquantity: "",
                            supplier: "",
                            productimage: null,
                            description: "",
                            stockthreshold: "",
                            currentImageUrl: null,
                            currentImageId: null,
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faClose} />
                      </button>
                    </div>

                    <form onSubmit={handleEdit} className="edit-patient-form">
                      <div className="form-group">
                        <label>Product Name *</label>
                        <input
                          type="text"
                          placeholder="Enter Product Name"
                          value={editProductForm.productname}
                          onChange={(e) =>
                            setEditProductForm({
                              ...editProductForm,
                              productname: e.target.value,
                            })
                          }
                          required
                        />
                      </div>

                      <div style={styles.formgroupwrapper}>
                        <div className="form-group">
                          <label>Category *</label>
                          <div style={styles.selectWithButton}>
                            <select
                              value={editProductForm.category}
                              onChange={(e) =>
                                setEditProductForm({
                                  ...editProductForm,
                                  category: e.target.value,
                                })
                              }
                              required
                              style={styles.selectField}
                            >
                              <option value="">Select Category</option>
                              {categoryItems.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              style={styles.inlineAddButton}
                              onClick={() => setShowAddCategoryModal(true)}
                              title="Add New Category"
                            >
                              <FontAwesomeIcon icon={faAdd} />
                            </button>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Supplier *</label>
                          <div style={styles.selectWithButton}>
                            <select
                              value={editProductForm.supplier}
                              onChange={(e) =>
                                setEditProductForm({
                                  ...editProductForm,
                                  supplier: e.target.value,
                                })
                              }
                              required
                              style={styles.selectField}
                            >
                              <option value="">Select Supplier</option>
                              {supplierItems.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              style={styles.inlineAddButton}
                              onClick={() => setShowAddSupplierModal(true)}
                              title="Add New Supplier"
                            >
                              <FontAwesomeIcon icon={faAdd} />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div style={styles.formgroupwrapper}>
                        <div className="form-group">
                          <label>SKU *</label>
                          <input
                            type="text"
                            placeholder="Enter Product SKU"
                            value={editProductForm.sku}
                            onChange={(e) =>
                              setEditProductForm({
                                ...editProductForm,
                                sku: e.target.value,
                              })
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Price (GHC) *</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Enter Product Price"
                            value={editProductForm.price}
                            onChange={(e) =>
                              setEditProductForm({
                                ...editProductForm,
                                price: e.target.value,
                              })
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Stock Quantity *</label>
                          <input
                            type="number"
                            placeholder="Enter Stock Quantity"
                            value={editProductForm.stockquantity}
                            onChange={(e) =>
                              setEditProductForm({
                                ...editProductForm,
                                stockquantity: e.target.value,
                              })
                            }
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Stock Threshold *</label>
                          <input
                            type="number"
                            placeholder="Enter Stock Threshold"
                            value={editProductForm.stockthreshold}
                            onChange={(e) =>
                              setEditProductForm({
                                ...editProductForm,
                                stockthreshold: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                      </div>

                      {/* Current Image Display */}
                      <div className="form-group">
                        <label>Current Product Image</label>
                        <div style={styles.currentImageContainer}>
                          <img
                            src={
                              editProductForm.currentImageUrl ||
                              defaultProductImg
                            }
                            alt="Current product"
                            style={styles.currentImage}
                            onError={(e) => {
                              e.target.src = defaultProductImg;
                            }}
                          />
                          {editProductForm.currentImageId && (
                            <button
                              type="button"
                              onClick={removeImageFromProduct}
                              style={styles.clearImageButton}
                              disabled={loading}
                            >
                              <FontAwesomeIcon icon={faTrashAlt} /> Remove
                              Current Image
                            </button>
                          )}
                        </div>
                        <p style={styles.imageHint}>
                          <FontAwesomeIcon icon={faImage} /> Current image will
                          be replaced if you upload a new one below
                        </p>
                      </div>

                      {/* Upload New Image */}
                      <div className="form-group">
                        <label>Upload New Image (Optional)</label>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setEditProductForm({
                                ...editProductForm,
                                productimage: file,
                              });
                            }
                          }}
                        />
                        {editProductForm.productimage && (
                          <div style={{ marginTop: "10px" }}>
                            <p
                              style={{
                                color: "#28a745",
                                fontSize: "12px",
                                marginBottom: "5px",
                              }}
                            >
                              New image selected:
                            </p>
                            <img
                              src={URL.createObjectURL(
                                editProductForm.productimage,
                              )}
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                borderRadius: "4px",
                                border: "2px solid #28a745",
                              }}
                            />
                            <p
                              style={{
                                fontSize: "12px",
                                marginTop: "5px",
                                color: "#666",
                              }}
                            >
                              Selected: {editProductForm.productimage.name}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Product Description</label>
                        <textarea
                          placeholder="Enter Product Description"
                          maxLength={500}
                          style={{ height: 80 }}
                          value={editProductForm.description}
                          onChange={(e) =>
                            setEditProductForm({
                              ...editProductForm,
                              description: e.target.value,
                            })
                          }
                        />
                      </div>

                      <div className="submitGroup">
                        <button
                          type="submit"
                          disabled={loading || uploadingImage}
                          style={styles.button}
                        >
                          {loading || uploadingImage ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            "Update Product"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        style={{
                          ...styles.pageButton,
                          ...(currentPage === pageNum
                            ? styles.activePageButton
                            : {}),
                        }}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  style={styles.paginationButton}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <div style={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faCancel}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
  },
  searchBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    gap: "20px",
  },
  searchContainer: {
    flex: 1,
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#666",
  },
  searchInput: {
    width: "100%",
    padding: "12px 12px 12px 40px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    boxSizing: "border-box",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  statIcon: {
    fontSize: "22px",
    color: "#007bff",
  },
  statValue: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "700",
    color: "#333",
  },
  statLabel: {
    margin: "5px 0 0 0",
    color: "#666",
    fontSize: "14px",
  },
  button: {
    background: "#333",
    color: "white",
    padding: "8px 24px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
  actionButtonsContainer: {
    display: "flex",
    gap: "15px",
    marginBottom: "20px",
    justifyContent: "flex-start",
  },
  actionButton: {
    background: "#6c757d",
    color: "white",
    padding: "10px 20px",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background 0.3s ease",
  },
  actionButtonIcon: {
    fontSize: "14px",
  },
  selectWithButton: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  selectField: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    fontSize: "14px",
  },
  inlineAddButton: {
    background: "#28a745",
    color: "white",
    padding: "8px 12px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  actionBTN: {
    display: "flex",
    gap: "8px",
  },
  printButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "6px",
    padding: "8px 12px",
    cursor: "pointer",
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid #e0e0e0",
  },
  paginationButton: {
    background: "#f8f9fa",
    border: "1px solid #dee2e6",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  pageNumbers: {
    display: "flex",
    gap: "8px",
  },
  pageButton: {
    background: "white",
    border: "1px solid #dee2e6",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    minWidth: "40px",
  },
  activePageButton: {
    background: "#007bff",
    color: "white",
    borderColor: "#007bff",
  },
  pageInfo: {
    color: "#666",
    fontSize: "14px",
  },
  formgroupwrapper: {
    display: "flex",
    gap: "10px",
  },
  prodimgNname: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  productImage: {
    width: "48px",
    height: "50px",
    objectFit: "cover",
    borderRadius: "6px",
  },
  currentImageContainer: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    marginBottom: "10px",
  },
  currentImage: {
    width: "100px",
    height: "100px",
    objectFit: "cover",
    borderRadius: "8px",
    border: "1px solid #ddd",
  },
  clearImageButton: {
    backgroundColor: "#dc3545",
    color: "white",
    padding: "8px 16px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
  imageHint: {
    fontSize: "12px",
    color: "#666",
    marginTop: "5px",
  },
};
